import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import {FaPeopleCarry, FaLock, FaFolderOpen, FaTrash, FaTimes, FaPlus, FaUser, FaMapMarkerAlt, FaPhone, FaArrowLeft, FaCalendarAlt, FaClock, FaUserTie, FaFileAlt } from "react-icons/fa";
import { IoTime } from "react-icons/io5";

import { FaPenToSquare } from "react-icons/fa6";
import { MdAssignmentAdd } from "react-icons/md";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { useGetProjetById }     from "../../hooks/PROJET/useGetProjetById";
import { useGetProjetEtapes }   from "../../hooks/PROJET/useGetProjetEtapes";

import { useCloseProjet }       from "../../hooks/PROJET/useCloseProjets";
import { useReopenProjet }      from "../../hooks/PROJET/useReopenProjet";
import {
  createEtape, deleteEtape,
  createIntervention, deleteIntervention,
} from "../../api/PROJETS/projets";
import { deleteProjet } from "../../api/PROJETS/projets";
import { formatDateShort } from "../../utils/date";
import { useUsers } from "../../hooks/useGetUsers";
import Select from "react-select";
import { customSelectStyles } from "../../utils/reactSelectStyles";
import MarkdownEditor from "../../components/MarkdownEditor/MarkdownEditor";
import MarkdownRenderer from "../../components/MarkdownEditor/MarkdownRenderer";
import { EditEtapeModal, EditInterventionModal, EditProjetModal } from "../../components/Modals/Update/EditProjetModals";
import ProjetFiles from "./ProjetFichiers";


// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUT_BADGE = {
  open:        { bg: "primary",   label: "Ouvert" },
  in_progress: { bg: "warning",   label: "En cours" },
  closed:      { bg: "secondary", label: "Clôturé" },
  approved:    { bg: "success",   label: "Approuvé" },
};

// Convertit "YYYY-MM-DD HH:MM" (datetime-local) en "YYYY-MM-DD HH:MM:00" (API)
const toApiDatetime = (s) => (s ? s.replace("T", " ") + ":00" : null);
const toApiDate = (s) => (s ? `${s} 00:00:00` : null);
const sortInterventionsByLatest = (interventions = []) => {
  return [...interventions].sort((a, b) => {
    const aTime = new Date(a?.start_date || a?.created_at || 0).getTime();
    const bTime = new Date(b?.start_date || b?.created_at || 0).getTime();
    return bTime - aTime;
  });
};

// ─── Modals ───────────────────────────────────────────────────────────────────

const NewEtapeModal = ({ projetId, show, onHide, onCreated }) => {
  const [titre, setTitre]         = useState("");
  const [description, setDesc]    = useState("");
  const [dateCloture, setDate]    = useState("");
  const [saving, setSaving]       = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titre.trim()) return toast.error("Le titre est requis");
    setSaving(true);
    try {
      await createEtape(projetId, {
        etape: { titre, description: description || null, date_cloture: toApiDate(dateCloture) || null },
      });
      toast.success("Étape ajoutée");
      setTitre(""); setDesc(""); setDate("");
      onCreated();
      onHide();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton><Modal.Title>Nouvelle étape</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Titre *</Form.Label>
            <Form.Control value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex : Phase 1 — Analyse" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <MarkdownEditor
              value={description}
              onChange={setDesc}
              rows={2}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Date de clôture prévue</Form.Label>
            <Form.Control type="date" value={dateCloture} onChange={(e) => setDate(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant="info" type="submit" disabled={saving}>{saving ? "Enregistrement..." : "Créer"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const NewInterventionModal = ({ projetId, etapes, show, onHide, onCreated, usersData }) => {
  const { user: currentUser }          = useAuth();
  const currentUserId                  = currentUser?.data?.id;
  const [description, setDesc]         = useState("");
  const [dateDebut, setDateDebut]      = useState("");
  const [duree, setDuree]              = useState("00:30");
  const [intervenantIds, setIntervenantIds] = useState([]);
  const [selectedEtape, setEtape]      = useState(null);
  const [saving, setSaving]            = useState(false);

  const userOptions  = (usersData?.data ?? []).filter(u => u.is_active).map(u => ({
    value: u.id, label: `${u.name} ${u.surname}`.trim(),
  }));
  const etapeOptions = (etapes ?? []).map(e => ({ value: e.id, label: e.title }));

  // Pré-sélectionner l'utilisateur connecté à chaque ouverture
  useEffect(() => {
    if (!show) return;
    setIntervenantIds(currentUserId ? [currentUserId] : []);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const convertDur = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim())         return toast.error("La description est requise");
    if (!dateDebut)                  return toast.error("La date est requise");
    if (intervenantIds.length === 0) return toast.error("Au moins un intervenant est requis");
    if (!selectedEtape)              return toast.error("L'étape est requise");

    setSaving(true);
    try {
      const baseData = {
        description,
        date_debut: toApiDatetime(dateDebut),
        duree: convertDur(duree),
        etape: selectedEtape.value,
      };
      await Promise.all(
        intervenantIds.map((userId) =>
          createIntervention(projetId, { intervention: { ...baseData, utilisateur: userId } })
        )
      );
      toast.success(intervenantIds.length > 1 ? "Interventions ajoutées" : "Intervention ajoutée");
      setDesc(""); setDateDebut(""); setDuree("00:30"); setIntervenantIds([]); setEtape(null);
      onCreated();
      onHide();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Modal show={show} onHide={onHide}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton><Modal.Title>Nouvelle intervention</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Étape *</Form.Label>
            <Select options={etapeOptions} value={selectedEtape} onChange={setEtape} styles={customSelectStyles} placeholder="Choisir une étape..." />
          </Form.Group>
          <Row className="mb-3">
            <Form.Group as={Col} md={7}>
              <Form.Label>Date et heure *</Form.Label>
              <Form.Control type="datetime-local" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
            </Form.Group>
            <Form.Group as={Col} md={5}>
              <Form.Label>Durée *</Form.Label>
              <Form.Control type="time" value={duree} onChange={(e) => setDuree(e.target.value)} />
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Intervenants *</Form.Label>
            <Select
              options={userOptions}
              value={userOptions.filter(o => intervenantIds.includes(o.value))}
              onChange={(opts) => setIntervenantIds(opts ? opts.map(o => o.value) : [])}
              styles={customSelectStyles}
              placeholder="Sélectionner un ou plusieurs intervenants..."
              isMulti
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description *</Form.Label>
            <MarkdownEditor
              value={description}
              onChange={setDesc}
              rows={3}
              placeholder="Détail des actions réalisées…"
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant="warning" type="submit" disabled={saving}>{saving ? "Enregistrement..." : "Ajouter"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ─── Onglet Étapes ────────────────────────────────────────────────────────────

// Couleurs progressives pour les étapes - nuances de cyan/info (du foncé au clair)
const STEP_COLORS = [
  "#3e106b", // Violet très foncé
  "#4a0d73", // Violet foncé
  "#6a1b9a", // Violet moyen-foncé
  "#8e44ad", // Violet 
  "#a569bd", // Violet moyen-clair
  "#c39bd3", // Violet clair
];

const getStepBorderColor = (index) => STEP_COLORS[Math.min(index, STEP_COLORS.length - 1)];

const EtapesTab = ({ projetId, hasPermission, usersData, isClosed, showNewEtape, setShowNewEtape, showNewIntervention, setShowNewIntervention }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetProjetEtapes(projetId);
  const etapes = data?.data?.project_stages ?? [];

  const [pendingEtapeId,         setPendingEtapeId]         = useState(null);
  const [pendingInterventionId,  setPendingInterventionId]  = useState(null);
  const [editingEtape,        setEditingEtape]        = useState(null); 
  const [editingIntervention, setEditingIntervention] = useState(null); 

  const reload = () => {
    queryClient.invalidateQueries({ queryKey: ["projetEtapes", projetId] });
  };

  const handleDeleteEtape = (stageId) => setPendingEtapeId(stageId);

  const doDeleteEtape = async () => {
    const stageId = pendingEtapeId;
    setPendingEtapeId(null);
    try {
      await deleteEtape(projetId, stageId);
      toast.success("Étape supprimée");
      reload();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    }
  };

  const handleDeleteIntervention = (intId) => setPendingInterventionId(intId);

  const doDeleteIntervention = async () => {
    const intId = pendingInterventionId;
    setPendingInterventionId(null);
    try {
      await deleteIntervention(projetId, intId);
      toast.success("Intervention supprimée");
      reload();
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    }
  };

  // ── Composant: Intervention Item (Card-based pour mobile) ──────────────────
  const InterventionItem = ({ intervention }) => {
    return (
      <Card className="mb-2 shadow-sm">
        <Card.Body className="d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-muted small">Date</div>
              <div className="small fw-semibold">{formatDateShort(intervention.start_date)}</div>
            </div>
            <div>
              <div className="text-muted small">Durée</div>
              <div className="small fw-semibold">{intervention.duration} min</div>
            </div>
            <div>
              <div className="text-muted small">Intervenant</div>
              <div className="small fw-semibold">{intervention.user ? `${intervention.user.name} ${intervention.user.surname}`.trim() : "—"}</div>
            </div>
          </div>
          {intervention.description && (
            <div>
              <div className="text-muted small">Description</div>
              <div className="small">
                <MarkdownRenderer content={intervention.description} />
              </div>
            </div>
          )}
          {!isClosed && hasPermission("DELETE_PROJECT_STEP") && (
            <div className="d-flex gap-2 mt-1 pt-2 border-top">
              <button className="btn btn-danger btn-sm p-1 flex-shrink-0" style={{ minWidth: "2rem", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleDeleteIntervention(intervention.id)} title="Supprimer">
                <FaTimes style={{ color: "#fff", fontSize: "0.875rem" }} />
              </button>
            </div>
          )}    
        </Card.Body>
      </Card>
    );
  };

  if (isLoading) return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;

  return (
    <>

      {etapes.length === 0 && (
        <p className="text-muted">Aucune étape définie pour ce projet.</p>
      )}

      {etapes.map((etape, index) => (
        <Card 
          key={etape.id} 
          className="mb-3 shadow-sm" 
          style={{ 
            borderLeft: `4px solid ${getStepBorderColor(index)}`,
            maxHeight: "450px", 
            display: "flex", 
            flexDirection: "column" 
          }}
        >
          <Card.Header className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2 flex-shrink-0">
            <span className="fw-semibold">{etape.title}</span>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {etape.close_date && (
                <small className="text-muted text-nowrap" style={{ fontSize: "0.8rem" }}>
                  Clôture : {formatDateShort(etape.close_date)}
                </small>
              )} 
              {!isClosed && hasPermission("EDIT_PROJECT_STEP") && (
                <Button variant="link" size="sm" className="text-info" onClick={() => setEditingEtape(etape)} >
                  <FaPenToSquare />
                </Button>
              )}
              {!isClosed && hasPermission("DELETE_PROJECT_STEP") && (
                <button 
                  className="btn btn-danger btn-sm p-1" 
                  style={{ minWidth: "2rem", display: "flex", alignItems: "center", justifyContent: "center" }} 
                  onClick={() => handleDeleteEtape(etape.id)} 
                  title="Supprimer étape"
                >
                  <FaTrash style={{ color: "#fff", fontSize: "0.875rem" }} />
                </button>
              )}
            </div>
          </Card.Header>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {etape.description && (
              <Card.Body className="py-2">
                <MarkdownRenderer content={etape.description} />
              </Card.Body>
            )}
            
            {etape.project_interventions?.length > 0 && (
              <Card.Body className="pt-0">
                {/* Mobile: Cards */}
                <div className="d-md-none">
                  <div className="mb-2">
                    <small className="text-muted fw-semibold">Interventions</small>
                  </div>
                  {sortInterventionsByLatest(etape.project_interventions).map((int) => (
                    <InterventionItem key={int.id} intervention={int} />
                  ))}
                </div>
                
                {/* Desktop: Table */}
                <div className="d-none d-md-block">
                  <table className="table table-sm table-hover table-striped mb-0">
                    <thead>
                      <tr>
                        <th style={{ borderBottom: "none", fontWeight: "normal" }}>
                          <div className="d-flex align-items-start">
                            <FaCalendarAlt className="text-muted mt-1 me-2" size={14} />
                            <div className="text-muted small">Date</div>
                          </div>
                        </th>
                        <th style={{ borderBottom: "none", fontWeight: "normal" }}>
                          <div className="d-flex align-items-start">
                            <FaClock className="text-muted mt-1 me-2" size={14} />
                            <div className="text-muted small">Durée (min)</div>
                          </div>
                        </th>
                        <th style={{ borderBottom: "none", fontWeight: "normal" }}>
                          <div className="d-flex align-items-start">
                            <FaUserTie className="text-muted mt-1 me-2" size={14} />
                            <div className="text-muted small">Intervenant</div>
                          </div>
                        </th>
                        <th style={{ borderBottom: "none", fontWeight: "normal" }}>
                          <div className="d-flex align-items-start">
                            <FaFileAlt className="text-muted mt-1 me-2" size={14} />
                            <div className="text-muted small">Description</div>
                          </div>
                        </th> 
                        {!isClosed && hasPermission("EDIT_INTERVENTION") && <th style={{ borderBottom: "none" }}></th>}
                        {!isClosed && hasPermission("DELETE_INTERVENTION") && <th style={{ borderBottom: "none" }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortInterventionsByLatest(etape.project_interventions).map((int) => (
                        <tr key={int.id}>
                          <td className="align-middle">{formatDateShort(int.start_date)}</td>
                          <td className="align-middle">{int.duration}</td>
                          <td className="align-middle">{int.user ? `${int.user.name} ${int.user.surname}`.trim() : "—"}</td>
                          <td className="align-middle">
                            {int.description
                              ? <MarkdownRenderer content={int.description} />
                              : <span className="text-muted">—</span>}
                          </td>
                          {!isClosed && hasPermission("EDIT_PROJECT_STEP") && hasPermission("EDIT_INTERVENTION") && (
                            <td className="text-center align-middle" style={{ width: "40px" }}>
                              <Button variant="link" size="sm" className="text-info p-0" onClick={() => setEditingIntervention(int)} title="Modifier">
                                <FaPenToSquare />
                              </Button>
                            </td>
                          )}
                          {!isClosed && hasPermission("DELETE_PROJECT_STEP") && hasPermission("DELETE_INTERVENTION") && (
                            <td className="text-center align-middle" style={{ width: "40px" }}>
                              <button 
                                className="btn btn-danger btn-sm p-0" 
                                style={{ width: "1.5rem", height: "1.5rem", display: "inline-flex", alignItems: "center", justifyContent: "center" }} 
                                onClick={() => handleDeleteIntervention(int.id)} 
                                title="Supprimer"
                              >
                                <FaTimes style={{ color: "#fff", fontSize: "0.875rem" }} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>  
                </div>
              </Card.Body>
            )}
          </div>
        </Card>
      ))}

      <NewEtapeModal
        projetId={projetId}
        show={showNewEtape}
        onHide={() => setShowNewEtape(false)}
        onCreated={reload}
      />
      <NewInterventionModal
        projetId={projetId}
        etapes={etapes}
        show={showNewIntervention}
        onHide={() => setShowNewIntervention(false)}
        onCreated={reload}
        usersData={usersData}
      />
      <ConfirmModal
        show={pendingEtapeId !== null}
        onHide={() => setPendingEtapeId(null)}
        onConfirm={doDeleteEtape}
        title="Supprimer l'étape"
        message="Êtes-vous sûr de vouloir supprimer cette étape ? Cette action est irréversible."
        confirmLabel="Supprimer"
        confirmVariant="danger"
      />
      <ConfirmModal
        show={pendingInterventionId !== null}
        onHide={() => setPendingInterventionId(null)}
        onConfirm={doDeleteIntervention}
        title="Supprimer l'intervention"
        message="Êtes-vous sûr de vouloir supprimer cette intervention ?"
        confirmLabel="Supprimer"
        confirmVariant="danger"
      />
          <EditEtapeModal
      projetId={projetId}
      etape={editingEtape}
      show={editingEtape !== null}
      onHide={() => setEditingEtape(null)}
      onUpdated={reload}
    />
    
    <EditInterventionModal
      projetId={projetId}
      intervention={editingIntervention}
      etapes={etapes}
      show={editingIntervention !== null}
      onHide={() => setEditingIntervention(null)}
      onUpdated={reload}
      usersData={usersData}
    />
    </>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────

const Projet = ({ titre }) => {
  const { projetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const fromList  = location.state?.from;
  const listState = location.state?.listState;

  const { data, isLoading, isError } = useGetProjetById(projetId);
  const { data: usersData }          = useUsers();
  const closeProjet   = useCloseProjet();
  const reopenProjet  = useReopenProjet();
  const queryClient   = useQueryClient();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showNewEtape, setShowNewEtape] = useState(false);
  const [showNewIntervention, setShowNewIntervention] = useState(false);
  const [showEditProjet, setShowEditProjet] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError || !data?.data) {
    return <p className="text-danger mt-4">Impossible de charger ce projet.</p>;
  }

  const projet = data.data;
  console.log("Données du projet :", projet);
  const badge  = STATUT_BADGE[projet.status] ?? { bg: "light", label: projet.status };
  const isClosed = projet.status === "closed" || projet.status === "approved";

  const handleClose = () => setShowConfirmClose(true);

  const doClose = async () => {
    setShowConfirmClose(false);
    try {
      await closeProjet.mutateAsync(projet.id);
      queryClient.invalidateQueries({ queryKey: ["projet", projetId] });
      toast.success("Projet clôturé");
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    }
  };

  const handleReopen = async () => {
    try {
      await reopenProjet.mutateAsync(projet.id);
      queryClient.invalidateQueries({ queryKey: ["projet", projetId] });
      toast.success("Projet réouvert");
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    }
  };
  const handleDeleteProjet = () => setShowConfirmDelete(true);

const doDeleteProjet = async () => {
  setShowConfirmDelete(false);
  try {
    console.log("Deleting projet ID:", projet.id);
    await deleteProjet(projet.id);
    toast.success("Projet supprimé");
    navigate("/projets");
  } catch (err) {
    console.error("Delete error:", err);
    toast.error(`Erreur : ${err.message}`);
  }
};
  return (
    <>
      {/* Back to list */}
      <Button variant="link" className="mb-2 ps-0 btn-topbar-icon" onClick={() => fromList ? navigate(fromList, { state: listState }) : navigate("/projets")}>
        <FaArrowLeft className="me-2" /> {fromList ? "Retour" : "Retour aux projets"}
      </Button>

      {/* Titre, Description, Statut et Dates - pleine largeur */}
      
      {/* ── HEADER ── */}
      <Card className="border-left-info shadow mt-3 mb-3">
        <Card.Body>
          <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center gap-2 flex-wrap">
            
            {/* Badge */}
            {!isClosed && (
              <span
                className={`badge bg-${badge.bg} flex-shrink-0`}
                style={{ fontSize: 12 }}
              >
                {badge.label}
              </span>
            )}

            {/* ID + Title */}
            <span
              className="d-flex align-items-center"
              style={{ wordBreak: "break-word", minWidth: 0 }}
            >
              <b className="me-2 flex-shrink-0">ID:{projet.id}</b>
              {projet.title}
            </span>

          </h1>
        </Card.Body>
      </Card>


      
       {/* Action buttons */}
      <div className="d-flex flex-wrap gap-2 mb-4 justify-content-end">
        {hasPermission("CREATE_PROJECT_STEP") && !isClosed && (
          <button className="btn btn-info btn-icon-split" onClick={() => setShowNewEtape(true)}>
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <FaPlus style={{ color: "#fff" }} />
            </span>
            <span className="text">Ajouter une étape</span>
          </button>
        )}
        {hasPermission("CREATE_PROJECT_STEP") && !isClosed && (
          <button className="btn btn-primary btn-icon-split" onClick={() => setShowNewIntervention(true)}>
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <MdAssignmentAdd style={{ color: "#fff" }} />
            </span>
            <span className="text">Ajouter une intervention</span>
          </button>
        )}
        {hasPermission("CLOSE_PROJECT") && !isClosed && (
          <button className="btn btn-warning btn-icon-split" onClick={handleClose}>
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <FaLock style={{ color: "#fff" }} />
            </span>
            <span className="text">Clôturer le projet</span>
          </button>
        )}
        {hasPermission("OPEN_PROJECT") && isClosed && (
          <button className="btn btn-success btn-icon-split" onClick={handleReopen}>
            <span className="icon text-white-50" style={{ alignItems: "center", justifyContent: "center" }}>
              <FaFolderOpen style={{ color: "#fff" }} />
            </span>
            <span className="text">Re-ouvrir</span>
          </button>
        )}
      </div>

      {/* ── INFORMATIONS ── */}
<Row>

  {/* ── COLONNE GAUCHE ── */}
  <Col xl={8}>

 <Card className="border-left-info shadow mb-4">
  <Card.Header className="d-flex justify-content-between align-items-center">
    <h6 className="text-info fw-semibold m-0">Information projet</h6>
    {!isClosed && hasPermission("EDIT_PROJECT") && (
      <Button variant="link" size="sm" className="text-info" onClick={() => setShowEditProjet(true)}>
        <FaPenToSquare />
      </Button>
    )}
  </Card.Header>

  <Card.Body>
    <Row className="mb-3 gy-3">
      <Col md={3}>
        <div className="d-flex align-items-start">
          <IoTime className="text-muted mt-1 me-1" size={16} />
          <div>
            <div className="text-muted small">Date début</div>
            <div>{formatDateShort(projet.start_date) || "—"}</div>
          </div>
        </div>
      </Col>
      <Col md={3}>
        <div className="d-flex align-items-start">
          <IoTime className="text-muted mt-1 me-1" size={16} />
          <div>
            <div className="text-muted small">Date de fin</div>
            <div>{formatDateShort(projet.end_date) || "—"}</div>
          </div>
        </div>
      </Col>
        <Col md={6}>
        <div className="d-flex align-items-start">
          <FaPeopleCarry className="text-muted mt-1 me-1" size={16} />
          <div style={{ minWidth: 0 }}>
            <div className="text-muted small">Assigné(s)</div>

            <div className="mt-1">
              {(projet.groups?.length > 0 || projet.users?.length > 0) ? (
                <>
                  {projet.groups?.map(g => (
                    <div key={`g-${g.id}`} className="mb-1">
                      <div 
                        className="badge bg-secondary text-white px-2 py-1"
                        style={{ borderRadius: "6px", fontSize: "0.75rem", fontWeight: "600", display: "inline-block" }}
                      >
                        {g.name}
                      </div>
                    </div>
                  ))}
                  {projet.users?.map(u => (
                    <div 
                      key={`u-${u.id}`} 
                      className="text-dark mb-1" 
                      style={{ fontSize: "0.9rem", lineHeight: "1.2" }}
                    >
                      {u.surname} {u.name}
                    </div>
                  ))}
                </>
              ) : (
                <span className="text-muted">Non assigné</span>
              )}
            </div>
          </div>
        </div>
      </Col>
    </Row>

    <hr />

    <div>
      <div className="text-muted small mb-1">Description</div>
      {projet.description ? (
        <MarkdownRenderer content={projet.description} />
      ) : (
        <span className="text-muted fst-italic">Pas d'information</span>
      )}
    </div>
  </Card.Body>
</Card>

      <Card className="shadow mb-4 border-left-info">

      {/* SECTION 2 */}
       <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="text-info fw-semibold m-0">Étapes & interventions</h6>
                    
                  </Card.Header>
      <Card.Body>

        <EtapesTab
          projetId={projetId}
          hasPermission={hasPermission}
          usersData={usersData}
          isClosed={isClosed}
          showNewEtape={showNewEtape}
          setShowNewEtape={setShowNewEtape}
          showNewIntervention={showNewIntervention}
          setShowNewIntervention={setShowNewIntervention}
        />
      </Card.Body>

    </Card>

  </Col>

  {/* ── COLONNE DROITE ── */}
  <Col xl={4}>

    <Card className="border-left-info shadow mb-3">
      <Card.Header>
        <h6 className="text-info fw-semibold m-0">Informations client</h6>
      </Card.Header>
      <Card.Body>
        <div className="mb-2"><FaUser className="me-2" /> {projet.client?.name || "Non renseigné"}</div>
        <div className="mb-2"><FaMapMarkerAlt className="me-2" /> {projet.client?.address || "Non renseigné"}</div>
        <div className="mb-2"><FaPhone className="me-2" /> {projet.client?.phone_number || "Non renseigné"}</div>
        <Button variant="info" className="w-100 mt-3" disabled={!projet.client} onClick={() => navigate(`/client/${projet.client.id}`)}>
          Voir le client
        </Button>
      </Card.Body>
    </Card>


    <Card className="border-left-info shadow">
      <Card.Header>
        <h6 className="text-info fw-semibold m-0">Fichiers joints</h6>
      </Card.Header>
      <Card.Body>
       <ProjetFiles
        projetId={projetId}
        canUpload={!isClosed && hasPermission("CREATE_FILE_PROJECT")}
        canDelete={!isClosed && hasPermission("DELETE_FILE_PROJECT")}
      />
      </Card.Body>
    </Card>
    {/* Danger zone */}
        {!isClosed && hasPermission("DELETE_PROJECT") && (
          <div className="mt-3">
            <Button
              variant="danger"
              className="w-100"
             onClick={() => {
            console.log("projet.id at click:", projet.id);
            handleDeleteProjet();
          }}
            >
              <FaTrash className="me-2" /> Supprimer le projet
            </Button>
          </div>
        )}
  </Col>

</Row>

      <ConfirmModal
        show={showConfirmClose}
        onHide={() => setShowConfirmClose(false)}
        onConfirm={doClose}
        title="Clôturer le projet"
        message={`Êtes-vous sûr de vouloir clôturer le projet « ${projet.title} » ?`}
        confirmLabel="Clôturer"
        confirmVariant="secondary"
      />

      <EditProjetModal
      projet={projet}
      show={showEditProjet}
      onHide={() => setShowEditProjet(false)}
      onUpdated={() => queryClient.invalidateQueries({ queryKey: ["projet", projetId] })}
    />
  <ConfirmModal
    show={showConfirmDelete}
    onHide={() => setShowConfirmDelete(false)}
    onConfirm={doDeleteProjet}
    title="Supprimer le projet"
    message={`Êtes-vous sûr de vouloir supprimer le projet « ${projet.title} » ? Cette action est irréversible.`}
    confirmLabel="Supprimer"
    confirmVariant="danger"
  />
    </>
  );
};

export default Projet;
